<script setup lang="ts">
import {
  CurrencyDisplay,
  formatting,
  Models,
  useDialogs,
  useLocalization,
  useOrderViewContext,
  apps
} from '@hantera/portal-app';
import { Badge, IconChevron } from '@hantera/design-system';
import { computed, ref, useTemplateRef, watch } from "vue";
import Breakdown from "@/components/Order/Deliveries/ShippingLine/Breakdown.vue";
import Decimal from "decimal.js";
import SelectShippingProduct from "@/components/Order/Deliveries/ShippingLine/SelectShippingProduct.vue";
import AppSlotView from '@/AppSlotView.vue';

const props = defineProps<{
  delivery: Models.Graph.Delivery
}>()

const orderViewContext = useOrderViewContext()
const expanded = ref(false);
const details = useTemplateRef<HTMLDivElement>('details');
const t = useLocalization('components.order.orderLine.details');
const dialogs = useDialogs()

const imageUrl = computed(() => {
  return '';
})

const displayFallbackImage = ref(false);

function onImageError() {
  displayFallbackImage.value = true
}

function toggleExpand() {
  expanded.value = !expanded.value;
}

const detailsStyle = ref<any>({});
let styleTimeout: ReturnType<typeof setTimeout> | undefined = undefined;
watch(expanded, v => {

  if (!v) {
    clearTimeout(styleTimeout);
    detailsStyle.value = {
      maxHeight: details.value?.scrollHeight + 'px',
    };
    styleTimeout = setTimeout(() => {
      detailsStyle.value = {};
    }, 0);
    return;
  }

  detailsStyle.value = {
    maxHeight: details.value?.scrollHeight + 'px',
  };

  styleTimeout = setTimeout(() => {
    detailsStyle.value = {
      maxHeight: 'none',
    };
  }, 100);

})

const discounted = computed(() => {
  if (props.delivery.calculatedShippingDiscounts.length === 0) return new Decimal(0);
  return Decimal.sum(...props.delivery.calculatedShippingDiscounts.map(r => r.value)).neg();
})

const allowEdit = computed(() => {
  return props.delivery.deliveryState === 'open'
})

function selectProduct() {
  dialogs.openModal(
    'Select shipping method',
    SelectShippingProduct,
    true,
    {
      orderViewContext,
      delivery: props.delivery
    }
  )
}

</script>

<template>
  <div
    class="shipping-line box-content"
    :class="{ 'shipping-line--expanded': expanded }"
  >
    <div class="shipping-line-primary p">
      <div class="shipping-line-primary-main">
        <div class="shipping-line-image-link box" v-if="displayFallbackImage">
          
        </div>
        <img
          :src="imageUrl"
          class="shipping-line-image box"
          @error="onImageError"
          v-else
        />
        <div class="shipping-line-product-number" v-if="allowEdit">
          <button class="btn-link" @click="selectProduct">{{ delivery.shippingProductNumber ?? (delivery.deliveryState === 'open' ? 'Select shipping method' : '') }}</button>
          <Badge v-if="delivery.releasedToInvoice">Invoice</Badge>
        </div>
        <div class="shipping-line-product-number" v-else>
          <div>{{ delivery.shippingProductNumber ?? (delivery.deliveryState === 'open' ? 'Select shipping method' : '') }}</div>
          <Badge v-if="delivery.releasedToInvoice">Invoice</Badge>
        </div>
        <div class="shipping-line-description">{{ delivery.shippingDescription }}</div>
      </div>
      <div class="detail">
        <div class="detail-label detail-unit-price">{{ t('unitPrice') }}</div>
        <div class="detail-value detail-unit-price">
          {{ formatting.currency(props.delivery.shippingPrice, orderViewContext.state.currencyCode, CurrencyDisplay.none) }}
        </div>
      </div>
      <div class="detail">
        <div class="detail-label detail-discount">{{ t('discount') }}</div>
        <div class="detail-value detail-discount">
          {{ formatting.currency(discounted, orderViewContext.state.currencyCode, CurrencyDisplay.none) }}
        </div>
      </div>
      <div class="detail" style="grid-column: calc(var(--grid-column-offset) - 3)">
        <div class="detail-label detail-sum">{{ t('sum') }}</div>
        <div class="detail-value detail-sum">
          {{ formatting.currency(props.delivery.shippingTotal, orderViewContext.state.currencyCode, CurrencyDisplay.none) }}
        </div>
      </div>
      <div class="shipping-line-expand-button" @click.prevent.stop="toggleExpand">
        <IconChevron class="shipping-line-expand-button-arrow" />
      </div>
    </div>
    <div
      class="shipping-line-details"
      ref="details"
      :style="detailsStyle"
    >
      <div class="shipping-line-details-margin">
        <div class="shipping-line-extras">
          <AppSlotView
            :slot="apps.orderViewSlots.shippingLine.extras"
            :context="{
              delivery
            }"
          />
        </div>
        <div class="shipping-line-breakdown">
          <Breakdown
            :delivery="delivery"
          />
        </div>
      </div>
    </div>
    
  </div>
</template>

<style scoped>
.shipping-line {
  display: grid;
  grid-column: 1 / -1;
  grid-template-columns: subgrid;
  --grid-column-offset: 0;
}

.shipping-line--expanded .shipping-line-expand-button-arrow {
  transform: rotate(-180deg);
}

.shipping-line :deep(.panel-content__body) {
  display: grid;
  grid-column: 1 / -1;
  grid-template-columns: subgrid;
}

.shipping-line-primary {
  display: grid;
  grid-column: 1 / -1;
  grid-template-columns: subgrid;
  grid-template-rows: min-content min-content;
  grid-auto-flow: column;
  align-items: center;
}

.shipping-line-primary-main {
  display: contents;
}

.shipping-line-details {
  display: grid;
  grid-column: 1 / -1;
  grid-template-columns: subgrid;

  overflow: hidden;
  transition: max-height 0.1s ease-out;
  max-height: 0;
  box-sizing: border-box;
}

.shipping-line-details-margin {
  display: grid;
  grid-column: 1 / -1;
  grid-template-columns: subgrid;
  border-top: 1px solid hsl(var(--color-70));
}

.shipping-line-extras {
  grid-column: 1 / middle;
}

.shipping-line-breakdown {
  grid-column: middle / calc(var(--grid-column-offset) - 2);
  display: grid;
  grid-template-columns: auto;
}

.shipping-line-breakdown:has(> div) {
  padding: 5px 0;
}

.shipping-line-image-link {
  grid-row: span 2;
  display: block;
  padding-bottom: 100%;
  position: relative;
}

.shipping-line-image {
  display: block;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.shipping-line-product-number {
  padding-left: 5px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: nowrap;
}

.shipping-line-description {
  grid-column: 2 / 3;
  grid-row: 2;
  padding-left: 5px;
}

:deep(.detail) {
  display: grid;
  grid-template-columns: auto;
  grid-template-rows: subgrid;
  grid-row: 1 / 3;
  align-items: center;
}

:deep(.detail-label) {
  white-space: nowrap;
  font-size: 12px;
  text-align: right;
  padding-left: 10px;
  color: hsl(var(--color-text-50));
}

:deep(.detail-value) {
  text-align: right;
  white-space: nowrap;
  padding-left: 10px;
}

:deep(.detail-value input) {
  height: 22px;
}

.shipping-line-expand-button {
  grid-column: -2;
  grid-row: 1 / 3;
  padding: 5px;
  margin-left: 5px;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  cursor: pointer;
}

.shipping-line-expand-button-arrow {
  width: 14px;
}

@container (max-width: 700px) {

  .shipping-line {
    --grid-column-offset: 1;
  }

  .shipping-line-primary-main {
    display: grid;
    grid-template-columns: 45px auto;
    grid-template-rows: min-content min-content;
    grid-column: 1 / -1;
    grid-row: 1 / 2;
    align-items: center;
  }

  :deep(.detail) {
    display: grid;
    grid-template-columns: auto;
    grid-template-rows: subgrid;
    grid-row: 3 / 5;
    align-items: center;
  }

  .shipping-line-expand-button {
    grid-column: -2;
    grid-row: 1 / 2;
  }

  .shipping-line-breakdown:has(> div) {
    padding: 5px;
  }
}
</style>